import {
  MedidaName,
  OrderedMedidas,
  SelectableOption,
} from '../../../../../../common/context/reducers/selectorReducer'
import {
  MatriculaMedidasItem,
  MatriculaTyreMeasure,
} from '../../../../../../common/types/MedidasPorMatricula'
import { NeumaticoMatricula } from '../../../../../../common/types/Neumatico'
import { PageMedida } from '../../../searchNeumaticos.store'

export type MatriculaMeasurePosition = 'front' | 'rear' | 'front_and_rear'

export interface MatriculaMeasureOption {
  id: string
  position: MatriculaMeasurePosition
  tyre: MatriculaTyreMeasure
}

type MedidasSeleccionadas = { [k in MedidaName]?: string | number | null }

const medidaHasValue = (value: string | number | null | undefined): boolean =>
  value !== null && value !== undefined && value !== ''

/** Alguns camps omplerts i altres no (p. ex. mida AMI/matricula incompleta). */
export function selectedMedidasArePartial(
  selected: MedidasSeleccionadas
): boolean {
  let filled = 0
  for (const name of OrderedMedidas) {
    if (medidaHasValue(selected[name])) filled += 1
  }
  return filled > 0 && filled < OrderedMedidas.length
}

/** Primer camp de la cadena sense valor (ancho → velocidad). */
export function findNextMedidaToComplete(
  selected: MedidasSeleccionadas
): MedidaName | null {
  for (const name of OrderedMedidas) {
    if (!medidaHasValue(selected[name])) return name
  }
  return null
}

/** Un camp només és seleccionable si tots els anteriors de la cadena tenen valor. */
export function isMedidaUnlocked(
  name: MedidaName,
  selected: MedidasSeleccionadas
): boolean {
  const index = OrderedMedidas.indexOf(name)
  if (index <= 0) return true
  for (let i = 0; i < index; i++) {
    if (!medidaHasValue(selected[OrderedMedidas[i]])) return false
  }
  return true
}

/** Elimina valors posteriors al primer camp pendent (p. ex. velocitat sense càrrega). */
export function stripInvalidDownstreamMedidas(
  selected: MedidasSeleccionadas
): MedidasSeleccionadas | null {
  const next = findNextMedidaToComplete(selected)
  if (!next) return null

  const nextIndex = OrderedMedidas.indexOf(next)
  let changed = false
  const result = { ...selected }

  for (let i = nextIndex + 1; i < OrderedMedidas.length; i++) {
    const name = OrderedMedidas[i]
    if (medidaHasValue(selected[name])) {
      result[name] = null
      changed = true
    }
  }

  return changed ? result : null
}

export function hasCompleteMedidasSelection(
  selected: MedidasSeleccionadas,
  pageMedida: PageMedida | null = null
): boolean {
  return OrderedMedidas.every((name) => medidaHasValue(selected[name])) || pageMedida != null
}

type LegacyMedidaField = {
  value?: string | number | null
  default?: string | number | null
}

/** Mides completes del selector legacy (funnel mobile des de home). */
export function buildSelectedMedidasFromLegacy(
  legacyMedidas:
    | { [k in MedidaName]?: LegacyMedidaField }
    | undefined
): MedidasSeleccionadas {
  const result: MedidasSeleccionadas = {}
  if (!legacyMedidas) return result

  for (const name of OrderedMedidas) {
    const medida = legacyMedidas[name]
    if (!medida) continue
    const value = medida.value ?? medida.default ?? null
    if (medidaHasValue(value)) {
      result[name] = value
    }
  }

  return result
}

export function catalogHasAnyMedidaSelection(
  selected: MedidasSeleccionadas
): boolean {
  return OrderedMedidas.some((name) => medidaHasValue(selected[name]))
}

/** No sobreescriure mides del catàleg en reobrir el modal del mateix vehicle. */
export function shouldPreserveCatalogMedidasOnVehicleReload(
  medidasState: {
    selectedMedidas: MedidasSeleccionadas
    isMedidasConfirmed: boolean
    medidasAlteredOnCatalog?: boolean
    vehicleTyreSelectionWasPartial?: boolean
    selectedMatriculaMeasureId?: string | null
    matriculaMedidas?: MatriculaMedidasItem[]
  }
): boolean {
  if (!catalogHasAnyMedidaSelection(medidasState.selectedMedidas)) {
    return false
  }

  const tyres = matriculaTyresFromItems(medidasState.matriculaMedidas ?? [])
  if (
    tyres.length > 0 &&
    shouldApplyPartialAmiInitialFromVehicle(
      medidasState.selectedMedidas,
      tyres,
      Boolean(medidasState.vehicleTyreSelectionWasPartial),
      {
        isMedidasConfirmed: medidasState.isMedidasConfirmed,
        medidasAlteredOnCatalog: medidasState.medidasAlteredOnCatalog,
      }
    )
  ) {
    return false
  }

  return (
    medidasState.isMedidasConfirmed ||
    selectedMedidasArePartial(medidasState.selectedMedidas) ||
    Boolean(medidasState.vehicleTyreSelectionWasPartial) ||
    Boolean(medidasState.selectedMatriculaMeasureId)
  )
}

export function catalogMatchesLegacyMedidas(
  catalogSelected: MedidasSeleccionadas,
  legacySelected: MedidasSeleccionadas
): boolean {
  return OrderedMedidas.every(
    (name) =>
      String(catalogSelected[name] ?? '') === String(legacySelected[name] ?? '')
  )
}

/** Prioritza mides confirmades al catàleg; si no, les del funnel legacy. */
export function resolveUserMedidasForMatriculaApply(
  catalogSelected: MedidasSeleccionadas,
  isMedidasConfirmed: boolean,
  legacySelected: MedidasSeleccionadas
): {
  selected: MedidasSeleccionadas
  treatAsUserChoice: boolean
  shouldSyncLegacyToCatalog: boolean
} {
  if (selectedMedidasArePartial(catalogSelected)) {
    return {
      selected: catalogSelected,
      treatAsUserChoice: true,
      shouldSyncLegacyToCatalog: false,
    }
  }

  const catalogComplete = hasCompleteMedidasSelection(catalogSelected)
  const legacyComplete = hasCompleteMedidasSelection(legacySelected)

  if (catalogComplete && isMedidasConfirmed) {
    return {
      selected: catalogSelected,
      treatAsUserChoice: true,
      shouldSyncLegacyToCatalog: false,
    }
  }

  if (legacyComplete) {
    return {
      selected: legacySelected,
      treatAsUserChoice: true,
      shouldSyncLegacyToCatalog:
        !catalogComplete || !isMedidasConfirmed,
    }
  }

  if (catalogComplete) {
    return {
      selected: catalogSelected,
      treatAsUserChoice: isMedidasConfirmed,
      shouldSyncLegacyToCatalog: false,
    }
  }

  return {
    selected: catalogSelected,
    treatAsUserChoice: false,
    shouldSyncLegacyToCatalog: false,
  }
}

function initialMedidasMatchOptions(
  derived: MedidasDerivadas
): boolean {
  return OrderedMedidas.every((name) => {
    const value = derived.initial[name]
    if (!medidaHasValue(value)) return false
    const options = derived.options[name]?.options ?? []
    return options.some((o) => String(o.value) === String(value))
  })
}

/** Mides per defecte aplicables sense triar al modal: un sol pneumàtic AMI o totes amb la mateixa mida completa. */
export function shouldAutoApplyMatriculaDefaults(
  derived: MedidasDerivadas | null,
  tyres: MatriculaTyreMeasure[]
): boolean {
  if (!derived || !tyres.length) return false
  if (!hasCompleteMedidasSelection(derived.initial)) return false
  if (!initialMedidasMatchOptions(derived)) return false
  if (tyres.length > 1 && !tyresShareSameFullDimensions(tyres)) return false
  return true
}

/**
 * Completa la cadena ancho→velocidad quan només falten camps i AMI en deixa un de clar
 * (p. ex. velocitat buida al tyres-with-values però coneguda a matrícula).
 */
export function tryCompleteMedidasFromMatricula(
  selected: MedidasSeleccionadas,
  matriculaMedidas: MatriculaMedidasItem[],
  seed?: MedidasSeleccionadas
): MedidasSeleccionadas | null {
  if (hasCompleteMedidasSelection(selected)) return null
  if (!matriculaMedidas.length) return null

  let next: MedidasSeleccionadas = { ...(seed ?? {}), ...selected }

  for (const name of OrderedMedidas) {
    if (medidaHasValue(next[name])) continue

    const opts = deriveMedidaOptionsFromMatriculaTyres(
      name,
      next,
      matriculaMedidas
    )
    if (opts.length !== 1) return null

    next[name] = opts[0].value
  }

  return hasCompleteMedidasSelection(next) ? next : null
}

interface MedidasDerivadas {
  initial: MedidasSeleccionadas
  options: {
    [k in MedidaName]?: {
      loading: boolean
      value: string | number | null
      options: SelectableOption<string | number | null>[]
    }
  }
}

export function extractMatriculaMeasureOptions(
  items: MatriculaMedidasItem[]
): MatriculaMeasureOption[] {
  const options: MatriculaMeasureOption[] = []

  items.forEach((item, index) => {
    if (item.front_and_rear) {
      options.push({
        id: `fr-${index}`,
        position: 'front_and_rear',
        tyre: item.front_and_rear,
      })
    }

    if (item.front) {
      options.push({
        id: `f-${index}`,
        position: 'front',
        tyre: item.front,
      })
    }

    if (item.rear) {
      options.push({
        id: `r-${index}`,
        position: 'rear',
        tyre: item.rear,
      })
    }
  })

  return options
}

const toNumberOrNull = (value: string | number | null | undefined): number | null => {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isNaN(parsed) ? null : parsed
}

function tyresShareSameFullDimensions(tyres: MatriculaTyreMeasure[]): boolean {
  if (tyres.length <= 1) return true
  const first = tyres[0]
  const load = toNumberOrNull(first.loadIndex)
  return tyres.every(
    (t) =>
      t.width === first.width &&
      t.aspectRatio === first.aspectRatio &&
      t.rimDiameter === first.rimDiameter &&
      toNumberOrNull(t.loadIndex) === load &&
      (t.speedIndex ?? null) === (first.speedIndex ?? null)
  )
}

function selectedMedidaValuesMatchTyre(
  selected: MedidasSeleccionadas,
  tyre: MatriculaTyreMeasure
): boolean {
  if (!hasCompleteMedidasSelection(selected)) return false

  return (
    toNumberOrNull(selected.ancho) === toNumberOrNull(tyre.width) &&
    toNumberOrNull(selected.serie) === toNumberOrNull(tyre.aspectRatio) &&
    toNumberOrNull(selected.llanta) === toNumberOrNull(tyre.rimDiameter) &&
    toNumberOrNull(selected.carga) === toNumberOrNull(tyre.loadIndex) &&
    String(selected.velocidad) === String(tyre.speedIndex ?? '')
  )
}

/** Coincidència de mida principal (ancho/sèrie/llanta), sense índex de càrrega ni velocitat. */
function selectedMedidaCoreFieldsMatchTyre(
  selected: MedidasSeleccionadas,
  tyre: MatriculaTyreMeasure
): boolean {
  return (
    medidaHasValue(selected.ancho) &&
    medidaHasValue(selected.serie) &&
    medidaHasValue(selected.llanta) &&
    toNumberOrNull(selected.ancho) === toNumberOrNull(tyre.width) &&
    toNumberOrNull(selected.serie) === toNumberOrNull(tyre.aspectRatio) &&
    toNumberOrNull(selected.llanta) === toNumberOrNull(tyre.rimDiameter)
  )
}

function selectedMedidaCoreSizeMatchTyre(
  selected: MedidasSeleccionadas,
  tyre: MatriculaTyreMeasure
): boolean {
  if (!hasCompleteMedidasSelection(selected)) return false
  return selectedMedidaCoreFieldsMatchTyre(selected, tyre)
}

export function findMatriculaMeasureIdForSelection(
  measures: MatriculaMeasureOption[],
  selected: MedidasSeleccionadas
): string | null {
  const found = measures.find(({ tyre }) =>
    selectedMedidaValuesMatchTyre(selected, tyre)
  )

  return found?.id ?? null
}

/** Destaca la mida AMI al modal: coincidència exacta o, si no, mida principal (ancho/sèrie/llanta). */
export function findMatriculaMeasureIdForCatalogSelection(
  measures: MatriculaMeasureOption[],
  selected: MedidasSeleccionadas
): string | null {
  if (!hasCompleteMedidasSelection(selected)) return null

  const exact = findMatriculaMeasureIdForSelection(measures, selected)
  if (exact) return exact

  const coreMatches = measures.filter(({ tyre }) =>
    selectedMedidaCoreSizeMatchTyre(selected, tyre)
  )
  if (!coreMatches.length) return null

  const homologatedCoreMatches = coreMatches.filter(
    ({ tyre }) => !amiTyreHasIncompleteHomologation(tyre)
  )
  if (!homologatedCoreMatches.length) return null

  const selectedLoad = toNumberOrNull(selected.carga)
  if (selectedLoad != null) {
    const withLoad = homologatedCoreMatches.find(
      ({ tyre }) => toNumberOrNull(tyre.loadIndex) === selectedLoad
    )
    if (withLoad) return withLoad.id
  }

  return homologatedCoreMatches[0]?.id ?? null
}

export function matriculaTyresHaveIncompleteHomologation(
  tyres: MatriculaTyreMeasure[]
): boolean {
  return tyres.some((tyre) => amiTyreHasIncompleteHomologation(tyre))
}

/** Mida principal AMI (ancho/sèrie/llanta) sense ic/iv per preomplir el catàleg. */
export function buildPartialAmiInitialFromDerived(
  derived: { initial: MedidasSeleccionadas } | null
): MedidasSeleccionadas | null {
  if (!derived) return null

  const { ancho, serie, llanta, carga, velocidad } = derived.initial
  if (
    !medidaHasValue(ancho) ||
    !medidaHasValue(serie) ||
    !medidaHasValue(llanta)
  ) {
    return null
  }

  if (medidaHasValue(carga) && medidaHasValue(velocidad)) {
    return null
  }

  return {
    ancho,
    serie,
    llanta,
    carga: null,
    velocidad: null,
  }
}

export function derivePartialDisplayMedidasFromMatriculaItems(
  items: MatriculaMedidasItem[]
): MedidasSeleccionadas | null {
  const tyres = matriculaTyresFromItems(items)
  if (!matriculaTyresHaveIncompleteHomologation(tyres)) return null
  return buildPartialAmiInitialFromDerived(deriveMedidasFromTyres(tyres))
}

/**
 * Selecció completa del funnel legacy (p. ex. 91/V per defecte) sobre mida AMI
 * incompleta amb la mateixa mida principal — no és una tria real de l'usuari.
 */
export function completeSelectionOverridesIncompleteAmiTyre(
  selected: MedidasSeleccionadas,
  tyres: MatriculaTyreMeasure[],
  vehicleTyreSelectionWasPartial = false
): boolean {
  if (vehicleTyreSelectionWasPartial && hasCompleteMedidasSelection(selected)) {
    return false
  }

  if (!hasCompleteMedidasSelection(selected)) return false
  if (selectedMedidasMatchSomeMatriculaTyre(selected, tyres)) return false

  return tyres.some(
    (tyre) =>
      amiTyreHasIncompleteHomologation(tyre) &&
      selectedMedidaCoreFieldsMatchTyre(selected, tyre)
  )
}

/**
 * Cal aplicar mides parcials AMI al catàleg (p. ex. després de defaults legacy a home per URL).
 */
export type ShouldApplyPartialAmiContext = {
  isMedidasConfirmed?: boolean
  medidasAlteredOnCatalog?: boolean
  legacySelected?: MedidasSeleccionadas
  preferVehiclePartialOverLegacy?: boolean
}

export function shouldApplyPartialAmiInitialFromVehicle(
  catalogSelected: MedidasSeleccionadas,
  tyres: MatriculaTyreMeasure[],
  vehicleTyreSelectionWasPartial = false,
  context: ShouldApplyPartialAmiContext = {}
): boolean {
  if (!matriculaTyresHaveIncompleteHomologation(tyres)) return false
  if (selectedMedidasArePartial(catalogSelected)) return false

  if (
    !context.preferVehiclePartialOverLegacy &&
    context.legacySelected &&
    hasCompleteMedidasSelection(catalogSelected) &&
    catalogMatchesLegacyMedidas(catalogSelected, context.legacySelected)
  ) {
    return false
  }

  if (
    vehicleTyreSelectionWasPartial &&
    hasCompleteMedidasSelection(catalogSelected)
  ) {
    return false
  }

  if (
    hasCompleteMedidasSelection(catalogSelected) &&
    (context.isMedidasConfirmed || context.medidasAlteredOnCatalog)
  ) {
    return false
  }

  if (!catalogHasAnyMedidaSelection(catalogSelected)) {
    return Boolean(context.preferVehiclePartialOverLegacy)
  }

  if (
    completeSelectionOverridesIncompleteAmiTyre(
      catalogSelected,
      tyres,
      vehicleTyreSelectionWasPartial
    )
  ) {
    return true
  }

  if (
    hasCompleteMedidasSelection(catalogSelected) &&
    !selectedMedidasMatchSomeMatriculaTyre(catalogSelected, tyres)
  ) {
    if (context.isMedidasConfirmed || context.medidasAlteredOnCatalog) {
      return false
    }

    if (
      context.legacySelected &&
      catalogMatchesLegacyMedidas(catalogSelected, context.legacySelected)
    ) {
      return Boolean(context.preferVehiclePartialOverLegacy)
    }

    return context.isMedidasConfirmed !== true
  }

  return false
}

/** AMI sense índex de càrrega o velocitat homologats (només mida principal). */
export function amiTyreHasIncompleteHomologation(
  tyre: MatriculaTyreMeasure
): boolean {
  const rawLoadMissing = tyre.loadIndex == null || tyre.loadIndex === ''
  const rawSpeedMissing = tyre.speedIndex == null || tyre.speedIndex === ''
  if (rawLoadMissing || rawSpeedMissing) {
    return true
  }
  return selectedMedidasArePartial(matriculaTyreToSelectedMedidas(tyre))
}

function matriculaTyresFromItems(
  matriculaMedidas: MatriculaMedidasItem[]
): MatriculaTyreMeasure[] {
  return extractMatriculaMeasureOptions(matriculaMedidas).map(
    (option) => option.tyre
  )
}

/** L'usuari completa ic/iv d'una mida AMI que només tenia ancho/sèrie/llanta. */
export function isCompletingHomologationForIncompleteAmiTyre(
  selected: MedidasSeleccionadas,
  matriculaMedidas: MatriculaMedidasItem[],
  fieldBeingSet?: MedidaName
): boolean {
  if (
    fieldBeingSet != null &&
    fieldBeingSet !== 'carga' &&
    fieldBeingSet !== 'velocidad'
  ) {
    return false
  }

  if (
    !medidaHasValue(selected.ancho) ||
    !medidaHasValue(selected.serie) ||
    !medidaHasValue(selected.llanta)
  ) {
    return false
  }

  return matriculaTyresFromItems(matriculaMedidas).some(
    (tyre) =>
      amiTyreHasIncompleteHomologation(tyre) &&
      selectedMedidaCoreFieldsMatchTyre(selected, tyre)
  )
}

export function selectedMedidasMatchSomeMatriculaTyre(
  selected: MedidasSeleccionadas,
  tyres: MatriculaTyreMeasure[]
): boolean {
  if (!tyres.length) return false
  return tyres.some((tyre) => selectedMedidaValuesMatchTyre(selected, tyre))
}

/** Per l’avís del catàleg: coincidència exacta amb alguna mida homologada del vehicle. */
export function selectedMedidasMatchSomeMatriculaTyreForVehicleWarning(
  selected: MedidasSeleccionadas,
  tyres: MatriculaTyreMeasure[]
): boolean {
  if (!hasCompleteMedidasSelection(selected)) return false

  return tyres.some((tyre) => {
    if (selectedMedidaValuesMatchTyre(selected, tyre)) return true
    if (
      amiTyreHasIncompleteHomologation(tyre) &&
      selectedMedidaCoreFieldsMatchTyre(selected, tyre)
    ) {
      return true
    }
    return false
  })
}

function tyreMatchesPriorMedidas(
  tyre: MatriculaTyreMeasure,
  selected: MedidasSeleccionadas,
  upToExclusiveIndex: number
): boolean {
  const { loadIndex, speedIndex } = resolveTyreLoadAndSpeed(tyre)

  for (let i = 0; i < upToExclusiveIndex; i++) {
    const name = OrderedMedidas[i]
    const value = selected[name]
    if (!medidaHasValue(value)) return false

    switch (name) {
      case 'ancho':
        if (toNumberOrNull(tyre.width) !== toNumberOrNull(value)) return false
        break
      case 'serie':
        if (toNumberOrNull(tyre.aspectRatio) !== toNumberOrNull(value))
          return false
        break
      case 'llanta':
        if (toNumberOrNull(tyre.rimDiameter) !== toNumberOrNull(value))
          return false
        break
      case 'carga':
        if (toNumberOrNull(loadIndex) !== toNumberOrNull(value)) return false
        break
      case 'velocidad':
        if (String(speedIndex ?? '') !== String(value)) return false
        break
    }
  }
  return true
}

/** Unió d'opcions per valor (API + AMI) sense duplicats. */
export function mergeMedidaOptionLists(
  ...lists: SelectableOption<string | number | null>[][]
): SelectableOption<string | number | null>[] {
  const seen = new Set<string>()
  const merged: SelectableOption<string | number | null>[] = []

  for (const list of lists) {
    for (const opt of list) {
      const key = String(opt.value)
      if (seen.has(key)) continue
      seen.add(key)
      merged.push(opt)
    }
  }

  return merged
}

/** Opcions d'un camp a partir de mides AMI/matricula quan tyres-with-values no en retorna. */
export function deriveMedidaOptionsFromMatriculaTyres(
  medidaName: MedidaName,
  selectedMedidas: MedidasSeleccionadas,
  matriculaMedidas: MatriculaMedidasItem[]
): SelectableOption<string | number | null>[] {
  const index = OrderedMedidas.indexOf(medidaName)
  if (index < 0 || !matriculaMedidas.length) return []

  const tyres = extractMatriculaMeasureOptions(matriculaMedidas).map(
    (option) => option.tyre
  )
  const matching = tyres.filter((tyre) =>
    tyreMatchesPriorMedidas(tyre, selectedMedidas, index)
  )

  const values = new Set<string | number>()
  matching.forEach((tyre) => {
    const { loadIndex, speedIndex } = resolveTyreLoadAndSpeed(tyre)

    switch (medidaName) {
      case 'ancho':
        if (typeof tyre.width === 'number') values.add(tyre.width)
        break
      case 'serie':
        if (typeof tyre.aspectRatio === 'number') values.add(tyre.aspectRatio)
        break
      case 'llanta':
        if (typeof tyre.rimDiameter === 'number') values.add(tyre.rimDiameter)
        break
      case 'carga': {
        const parsedLoad = toNumberOrNull(loadIndex)
        if (parsedLoad !== null) values.add(parsedLoad)
        break
      }
      case 'velocidad':
        if (speedIndex) values.add(speedIndex)
        break
    }
  })

  const sorted =
    medidaName === 'velocidad'
      ? Array.from(values).sort((a, b) => String(a).localeCompare(String(b)))
      : Array.from(values).sort((a, b) => Number(a) - Number(b))

  return sorted.map((value) => ({
    label: String(value),
    value,
  }))
}

/** Compara snapshots de mides ignorant diferències string/number (p. ex. carga 105 vs "105"). */
export function medidasSnapshotsEqual(
  a: MedidasSeleccionadas | undefined,
  b: MedidasSeleccionadas | undefined
): boolean {
  if (!a || !b) return false
  return OrderedMedidas.every((name) => {
    const va = a[name]
    const vb = b[name]
    const aEmpty = va == null || va === ''
    const bEmpty = vb == null || vb === ''
    if (aEmpty && bEmpty) return true
    if (aEmpty || bEmpty) return false
    if (name === 'velocidad') return String(va) === String(vb)
    return toNumberOrNull(va) === toNumberOrNull(vb)
  })
}

export function deriveMedidasFromTyres(
  tyres: MatriculaTyreMeasure[]
): MedidasDerivadas | null {
  const seen = {
    ancho: new Set<number>(),
    serie: new Set<number>(),
    llanta: new Set<number>(),
    carga: new Set<number>(),
    velocidad: new Set<string>(),
  }

  tyres.forEach((tyre) => {
    const { loadIndex, speedIndex } = resolveTyreLoadAndSpeed(tyre)

    if (typeof tyre.width === 'number') seen.ancho.add(tyre.width)
    if (typeof tyre.aspectRatio === 'number') seen.serie.add(tyre.aspectRatio)
    if (typeof tyre.rimDiameter === 'number') seen.llanta.add(tyre.rimDiameter)
    const parsedLoad = toNumberOrNull(loadIndex)
    if (parsedLoad !== null) seen.carga.add(parsedLoad)
    if (speedIndex) seen.velocidad.add(speedIndex)
  })

  const sortedNumbers = (values: Set<number>) =>
    Array.from(values).sort((a, b) => a - b)
  const sortedStrings = (values: Set<string>) =>
    Array.from(values).sort((a, b) => a.localeCompare(b))

  const firstTyre = tyres[0]
  if (!firstTyre) return null

  const { loadIndex: firstLoadIndex, speedIndex: firstSpeedIndex } =
    resolveTyreLoadAndSpeed(firstTyre)

  return {
    initial: {
      ancho: firstTyre.width ?? null,
      serie: firstTyre.aspectRatio ?? null,
      llanta: firstTyre.rimDiameter ?? null,
      carga: toNumberOrNull(firstLoadIndex),
      velocidad: firstSpeedIndex,
    },
    options: {
      ancho: {
        loading: false,
        value: null,
        options: sortedNumbers(seen.ancho).map((value) => ({
          label: String(value),
          value,
        })),
      },
      serie: {
        loading: false,
        value: null,
        options: sortedNumbers(seen.serie).map((value) => ({
          label: String(value),
          value,
        })),
      },
      llanta: {
        loading: false,
        value: null,
        options: sortedNumbers(seen.llanta).map((value) => ({
          label: String(value),
          value,
        })),
      },
      carga: {
        loading: false,
        value: null,
        options: sortedNumbers(seen.carga).map((value) => ({
          label: String(value),
          value,
        })),
      },
      velocidad: {
        loading: false,
        value: null,
        options: sortedStrings(seen.velocidad).map((value) => ({
          label: value,
          value,
        })),
      },
    },
  }
}

export function neumaticoMatriculaHasValidMedidas(
  neumatico: NeumaticoMatricula | null | undefined
): boolean {
  if (!neumatico) return false
  const ancho = Number(neumatico.ancho)
  const serie = Number(neumatico.serie)
  const llanta = Number(neumatico.llanta)
  if (Number.isNaN(ancho) || Number.isNaN(serie) || Number.isNaN(llanta)) {
    return false
  }
  const hasLoad = neumatico.ic != null && neumatico.ic !== ''
  const hasSpeed = neumatico.cv != null && neumatico.cv !== ''
  return hasLoad && hasSpeed
}

function resolveTyreLoadAndSpeed(tyre: MatriculaTyreMeasure): {
  loadIndex: string | null
  speedIndex: string | null
} {
  let loadIndex = tyre.loadIndex ?? null
  let speedIndex = tyre.speedIndex ?? null

  if ((!loadIndex || !speedIndex) && (tyre.original || tyre.normalized)) {
    const source = `${tyre.original ?? ''} ${tyre.normalized ?? ''}`
    const match = source.match(/R\d{1,2}\s*(\d{2,3})\s*([A-Z])\b/i)
    if (match) {
      if (!loadIndex) loadIndex = match[1]
      if (!speedIndex) speedIndex = match[2]
    }
  }

  return { loadIndex, speedIndex }
}

export function matriculaTyreToSelectedMedidas(
  tyre: MatriculaTyreMeasure
): MedidasSeleccionadas {
  const { loadIndex, speedIndex } = resolveTyreLoadAndSpeed(tyre)
  const carga = toNumberOrNull(loadIndex)

  return {
    ancho: tyre.width ?? null,
    serie: tyre.aspectRatio ?? null,
    llanta: tyre.rimDiameter ?? null,
    carga,
    velocidad: speedIndex,
  }
}

export function neumaticoMatriculaToTyreMeasure(
  neumatico: NeumaticoMatricula
): MatriculaTyreMeasure {
  const loadAndSpeed = `${neumatico.ic ?? ''}${neumatico.cv ?? ''}`.trim()
  const base = `${neumatico.ancho}/${neumatico.serie} ${neumatico.estructura}${neumatico.llanta}`
  return {
    original: `${base} ${loadAndSpeed}`.trim(),
    normalized: loadAndSpeed ? `${base} ${loadAndSpeed}` : base,
    format: 'metric',
    width: Number(neumatico.ancho),
    aspectRatio: Number(neumatico.serie),
    construction: 'R',
    rimDiameter: Number(neumatico.llanta),
    loadIndex: neumatico.ic || null,
    speedIndex: neumatico.cv || null,
    dualLoadIndex: null,
    extraLoad: false,
    runFlat: neumatico.runflat ?? false,
    season: null,
    plyRating: null,
    highPerformance: false,
    isCommercial: false,
    isTemporary: false,
  }
}

export function neumaticoMatriculaToSelectedMedidas(
  neumatico: NeumaticoMatricula
): MedidasSeleccionadas {
  const parsedCarga = toNumberOrNull(neumatico.ic)
  return {
    ancho: Number(neumatico.ancho),
    serie: Number(neumatico.serie),
    llanta: Number(neumatico.llanta),
    carga: parsedCarga,
    velocidad: neumatico.cv || null,
  }
}

/** Coincidència física (ancho/serie/llanta) per filtrar AMI amb mida de taller. */
function matriculaTyresMatchPhysical(
  a: MatriculaTyreMeasure,
  b: MatriculaTyreMeasure
): boolean {
  return (
    a.width === b.width &&
    a.aspectRatio === b.aspectRatio &&
    a.rimDiameter === b.rimDiameter
  )
}

function filterMedidasItemForWorkshopTyre(
  item: MatriculaMedidasItem,
  workshopTyre: MatriculaTyreMeasure
): MatriculaMedidasItem | null {
  // Muntatge escalonat: si una eix coincideix amb taller, conservar el parell AMI complet
  if (item.front && item.rear) {
    const frontMatches = matriculaTyresMatchPhysical(item.front, workshopTyre)
    const rearMatches = matriculaTyresMatchPhysical(item.rear, workshopTyre)
    if (frontMatches || rearMatches) {
      return { front: item.front, rear: item.rear }
    }
    return null
  }

  const filtered: MatriculaMedidasItem = {}

  if (
    item.front_and_rear &&
    matriculaTyresMatchPhysical(item.front_and_rear, workshopTyre)
  ) {
    filtered.front_and_rear = item.front_and_rear
  }
  if (item.front && matriculaTyresMatchPhysical(item.front, workshopTyre)) {
    filtered.front = item.front
  }
  if (item.rear && matriculaTyresMatchPhysical(item.rear, workshopTyre)) {
    filtered.rear = item.rear
  }

  if (
    !filtered.front_and_rear &&
    !filtered.front &&
    !filtered.rear
  ) {
    return null
  }

  return filtered
}

/**
 * Si hay medida de taller conocida, deja opciones AMI con la misma medida física
 * (ancho/serie/llanta); en muntatge escalonat conserva el parell complet.
 * Si ninguna coincide, expone únicamente la medida de taller (sintética).
 */
export function filterMatriculaMedidasForWorkshopTyre(
  items: MatriculaMedidasItem[],
  workshopTyre: MatriculaTyreMeasure
): MatriculaMedidasItem[] {
  const filtered = items
    .map((item) => filterMedidasItemForWorkshopTyre(item, workshopTyre))
    .filter((item): item is MatriculaMedidasItem => item != null)

  if (filtered.length > 0) return filtered

  return [{ front_and_rear: workshopTyre }]
}

export function formatMatriculaTyreLabel(tyre: MatriculaTyreMeasure): string {
  const { loadIndex, speedIndex } = resolveTyreLoadAndSpeed(tyre)
  const loadAndSpeed = `${loadIndex ?? ''}${speedIndex ?? ''}`.trim()

  if (
    typeof tyre.width === 'number' &&
    typeof tyre.aspectRatio === 'number' &&
    typeof tyre.rimDiameter === 'number'
  ) {
    const base = `${tyre.width}/${tyre.aspectRatio} R${tyre.rimDiameter}`
    return loadAndSpeed ? `${base} ${loadAndSpeed}` : base
  }

  if (tyre.original) return tyre.original
  if (tyre.normalized) return tyre.normalized
  return '-'
}
